import { defineQuery } from "groq";

// Shared card projection (reused in several queries)
const CARD_PROJECTION = `
  _id,
  title,
  "slug": slug.current,
  dek,
  featured,
  publishedAt,
  coverImage{
    alt,
    caption,
    "url": asset->url
  },
  "author": author->{
    name,
    "slug": slug.current,
    avatar{
      alt,
      "url": asset->url
    }
  },
  "categories": coalesce(categories[]->{
    name,
    "slug": slug.current,
    "color": color.hex
  }, []),
  "primaryTag": primaryTag->{
    name,
    "slug": slug.current,
    colorKey
  },
  "tags": coalesce(tags[]->{
    name,
    "slug": slug.current,
    colorKey
  }, [])
`;

const PUBLISHED_ARTICLE = `_type == "article" && !(_id in path("drafts.**"))`;

/** Card list shared by the index query and allInsightsQuery. */
const INSIGHT_CARD_LIST = `*[
  ${PUBLISHED_ARTICLE}
] | order(coalesce(featured, false) desc, publishedAt desc) {
  ${CARD_PROJECTION}
}`;

/** "Latest in [Category]" rows — ≥3 articles, top 4 by latestAt, 3 cards each. */
const CATEGORY_ROWS = `*[
  _type == "category"
  && defined(slug.current)
  && count(*[
    ${PUBLISHED_ARTICLE}
    && references(^._id)
  ]) >= 3
] {
  "slug": slug.current,
  name,
  "color": color.hex,
  "latestAt": *[
    ${PUBLISHED_ARTICLE}
    && references(^._id)
  ] | order(publishedAt desc)[0].publishedAt,
  "articles": *[
    ${PUBLISHED_ARTICLE}
    && references(^._id)
  ] | order(coalesce(featured, false) desc, publishedAt desc)[0...3] {
    ${CARD_PROJECTION}
  }
} | order(latestAt desc)[0...4]`;

/**
 * Sidebar taxonomy block — categories/types with counts, top tags, allCount.
 * Tag `count` is only used for ordering; loaders strip it for the UI.
 */
const SIDEBAR_PROJECTION = `
  "categories": *[
    _type == "category"
    && defined(slug.current)
  ] | order(name asc) {
    "slug": slug.current,
    name,
    "color": color.hex,
    "count": count(*[
      ${PUBLISHED_ARTICLE}
      && references(^._id)
    ])
  },
  "types": *[
    _type == "articleType"
    && defined(slug.current)
  ] | order(name asc) {
    "slug": slug.current,
    "name": title,
    "count": count(*[
      ${PUBLISHED_ARTICLE}
      && references(^._id)
    ])
  },
  "tags": *[
    _type == "tag"
    && defined(slug.current)
    && count(*[
      ${PUBLISHED_ARTICLE}
      && references(^._id)
    ]) > 0
  ] {
    "slug": slug.current,
    name,
    "count": count(*[
      ${PUBLISHED_ARTICLE}
      && references(^._id)
    ])
  } | order(count desc)[0...7],
  "allCount": count(*[${PUBLISHED_ARTICLE}])
`;

export const allInsightsQuery = defineQuery(INSIGHT_CARD_LIST);

/** RSS feed projection — latest published articles, newest first (see routes/insightsFeed.tsx). */
export const insightsFeedQuery = defineQuery(`*[
  _type == "article"
  && !(_id in path("drafts.**"))
  && defined(slug.current)
  && defined(publishedAt)
] | order(publishedAt desc)[0...50] {
  title,
  "slug": slug.current,
  dek,
  publishedAt,
  "coverImage": coverImage.asset->url,
  "author": author->name,
  "categories": coalesce(categories[]->name, [])
}`);

/** Compact projection for dashboard Highlights Row — latest published article only. */
export const latestInsightQuery = defineQuery(`*[
  _type == "article"
  && !(_id in path("drafts.**"))
] | order(publishedAt desc) [0] {
  title,
  "slug": slug.current,
  publishedAt
}`);

/**
 * Insights index — one round-trip for the card feed, category rows, and sidebar.
 */
export const insightsIndexQuery = defineQuery(`{
  "items": ${INSIGHT_CARD_LIST},
  "categoryRows": ${CATEGORY_ROWS},
  "sidebar": {
    ${SIDEBAR_PROJECTION}
  }
}`);

/**
 * Sidebar-only payload for category/type/tag listing pages
 * (listing + optional meta + this = ≤2–3 Sanity calls).
 */
export const insightsSidebarQuery = defineQuery(`{
  ${SIDEBAR_PROJECTION}
}`);

const INSIGHT_DETAIL_PROJECTION = `
  _id,
  title,
  "slug": slug.current,
  dek,
  featured,
  publishedAt,
  updatedAt,
  coverImage{
    alt,
    caption,
    "url": asset->url
  },
  "author": author->{
    _id,
    "slug": slug.current,
    name,
    avatar{
      alt,
      "url": asset->url
    }
  },
  "categories": coalesce(categories[]->{
    _id,
    "slug": slug.current,
    name,
    "color": color.hex
  }, []),
  "primaryTag": primaryTag->{
    _id,
    "slug": slug.current,
    name,
    description,
    colorKey
  },
  "tags": coalesce(tags[]->{
    _id,
    "slug": slug.current,
    name,
    description,
    colorKey
  }, []),
  body[]{
    ...,
    _type == "imageWithAlt" => {
      ...,
      "url": asset->url,
      alt,
      caption
    },
    _type == "faqGroup" => {
      ...,
      // Skip refs that do not resolve (e.g. a private faq.<hash> read without
      // the token) — a null item breaks the FAQ block and its JSON-LD.
      items[defined(@->_id)]->{
        _id,
        question,
        answer
      }
    },
    _type == "rawHtml" => {
      ...,
      "html": snippet->html
    },
    _type == "htmlArchiveEmbed" => {
      ...,
      "archive": archive->{
        title,
        entryFile,
        "url": archive.asset->url
      }
    },
    _type == "livePredictionData" => {
      ...,
      eventGroupSlug,
      parameter,
      eventId,
      marketId
    }
  },
  "relatedArticles": relatedArticles[]->{
    ${CARD_PROJECTION}
  },
  referencedEvents,
  seo
`;

export const insightBySlugQuery = defineQuery(`*[
  _type == "article"
  && slug.current == $slug
  && !(_id in path("drafts.**"))
][0] {
  ${INSIGHT_DETAIL_PROJECTION}
}`);

export const insightDraftBySlugQuery = defineQuery(`coalesce(
  *[
    _type == "article"
    && slug.current == $slug
    && _id in path("drafts.**")
  ][0],
  *[
    _type == "article"
    && slug.current == $slug
  ][0]
) {
  ${INSIGHT_DETAIL_PROJECTION}
}`);

export const relatedArticlesQuery = defineQuery(`*[
  _type == "article"
  && !(_id in path("drafts.**"))
  && _id != $articleId
  && $categoryId in categories[]._ref
] | order(publishedAt desc) [0...3] {
  ${CARD_PROJECTION}
}`);

const INSIGHTS_SEARCH_LIMIT = 50;

export const insightsSearchQuery = defineQuery(`*[
  ${PUBLISHED_ARTICLE}
  && title match $pattern
] | order(publishedAt desc)[0...${INSIGHTS_SEARCH_LIMIT}] {
  ${CARD_PROJECTION}
}`);

// Category pills for nav — all Sanity categories
export const allInsightCategoriesQuery = defineQuery(`*[
  _type == "category"
  && defined(slug.current)
] | order(name asc) {
  "slug": slug.current,
  name,
  "color": color.hex
}`);

// Articles filtered by category slug
export const insightsByCategorySlugQuery = defineQuery(`*[
  _type == "article"
  && !(_id in path("drafts.**"))
  && $categorySlug in categories[]->slug.current
] | order(coalesce(featured, false) desc, publishedAt desc) {
  ${CARD_PROJECTION}
}`);

/** Type listing page — insights + type meta in one round-trip. */
export const insightsTypePageQuery = defineQuery(`{
  "insights": *[
    _type == "article"
    && !(_id in path("drafts.**"))
    && articleType->slug.current == $typeSlug
  ] | order(coalesce(featured, false) desc, publishedAt desc) {
    ${CARD_PROJECTION}
  },
  "type": *[
    _type == "articleType"
    && slug.current == $typeSlug
  ][0] {
    "slug": slug.current,
    "name": title,
    description
  }
}`);

/** Tag listing page — insights + tag meta in one round-trip. */
export const insightsTagPageQuery = defineQuery(`{
  "insights": *[
    _type == "article"
    && !(_id in path("drafts.**"))
    && (
      $tagSlug in tags[]->slug.current
      || primaryTag->slug.current == $tagSlug
    )
  ] | order(coalesce(featured, false) desc, publishedAt desc) {
    ${CARD_PROJECTION}
  },
  "tag": *[
    _type == "tag"
    && slug.current == $tagSlug
  ][0] {
    "slug": slug.current,
    name,
    colorKey
  }
}`);
